import type { OrgMember, Organization } from '@/types/database.types';

export type OperationalOrganization = Pick<
  Organization,
  | 'id'
  | 'name'
  | 'owner_user_id'
  | 'company_logo_url'
  | 'currency_code'
  | 'theme'
  | 'shift_settings'
  | 'trial_ends_at'
  | 'subscription_status'
  | 'subscription_tier'
  | 'subscription_current_period_end'
  | 'created_at'
  | 'updated_at'
> & Partial<Pick<Organization, 'timezone'>>;

export const ORGANIZATION_CONTEXT_SELECT = `
  id,
  name,
  owner_user_id,
  company_logo_url,
  currency_code,
  theme,
  shift_settings,
  timezone,
  trial_ends_at,
  subscription_status,
  subscription_tier,
  subscription_current_period_end,
  created_at,
  updated_at
`;

export const ORGANIZATION_CONTEXT_SELECT_WITHOUT_TIMEZONE = `
  id,
  name,
  owner_user_id,
  company_logo_url,
  currency_code,
  theme,
  shift_settings,
  trial_ends_at,
  subscription_status,
  subscription_tier,
  subscription_current_period_end,
  created_at,
  updated_at
`;

export function isMissingOrganizationTimezoneError(error: unknown) {
  if (!error || typeof error !== 'object') return false;

  const candidate = error as { code?: string; message?: string; details?: string; hint?: string };
  const description = [candidate.message, candidate.details, candidate.hint]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return (candidate.code === '42703' || candidate.code === 'PGRST204')
    && description.includes('timezone');
}

export function canLoadSensitiveOrganizationFields(role: OrgMember['role']) {
  return role === 'BO';
}

export function buildOperationalOrganization(organization: OperationalOrganization): Organization {
  return {
    ...organization,
    timezone: organization.timezone ?? 'Australia/Sydney',
    // Deliberately absent from the context select: a column the deployed frontend asks
    // for before its migration has been applied breaks the whole workspace load. The
    // compliance panel reads this through its own tolerant query instead.
    is_small_business_employer: null,
    // Whether a card is still needed is read from organization_access instead, which the
    // billing screen asks for directly; these keep the shape without widening that select.
    access_requires_payment_method: false,
    trial_days: 30,
    abn: null,
    legal_name: null,
    trading_name: null,
    abn_verified_at: null,
    business_email: null,
    business_phone: null,
    business_address: null,
    bank_name: null,
    bank_bsb: null,
    bank_account_number: null,
    bank_account_name: null,
    payment_terms: null,
    invoice_settings: null,
    stripe_customer_id: null,
    stripe_subscription_id: null,
    // Read by useBusinessVerification, which tolerates the column not existing yet.
    business_verification: 'VERIFIED',
    business_verification_reason: null,
    business_verified_at: null,
    business_verified_by: null,
  };
}
